import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { ButtonComp, CardComp, TextInputComp, FormikTextAreaComp } from "../../../components";
import FallbackImage from "../../../components/Common/ImageWrapper";
import { PageTitle } from "../modules/PageHeaders";
import { useUser } from "../../../hooks";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { useUpdatePortalMutation } from "../../../store/admin/settings/portalSlice";
import { setCoopData, setUserData } from "../../../store/auth/index";
import uploadImage from "../../../utils/uploadImage";
import ThemeBrandingEditor from "../../../components/Common/ThemeBrandingEditor";
import DraftThemeApplicator from "../../../components/Common/DraftThemeApplicator";
import {
  DEFAULT_THEME,
  applyCompanyTheme,
  mergeThemeIntoCompany,
  normalizeTheme,
  persistThemeFromCoopPayload,
  themeToApiPayload,
} from "../../../theme/companyTheme";

const phoneRegExp = /^([0]{1})[0-9]{10}$/;
const hexColorRegExp = /^#[0-9A-Fa-f]{6}$/;

const getSavedThemeValues = (company) => {
  const theme = normalizeTheme(company);
  return {
    primary_color: theme.primary,
    secondary_color: theme.secondary,
  };
};

export default function ConfigurationHome() {
  const [isEditing, setIsEditing] = useState(false);
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const user = useUser();
  const dispatch = useDispatch();
  const [updatePortal, { isLoading: isSubmitting }] = useUpdatePortalMutation();

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Company name is required")
      .min(2, "Company name must be at least 2 characters"),
    address: Yup.string()
      .required("Address is required")
      .min(5, "Address must be at least 5 characters"),
    phone: Yup.string()
      .matches(phoneRegExp, "Phone number must be 11 digits starting with 0")
      .required("Phone number is required"),
    primary_color: Yup.string()
      .required("Primary color is required")
      .matches(hexColorRegExp, "Enter a valid hex color"),
    secondary_color: Yup.string()
      .required("Secondary color is required")
      .matches(hexColorRegExp, "Enter a valid hex color"),
  });

  const initialValues = {
    name: user?.company?.name || "",
    address: user?.company?.address || "",
    phone: user?.company?.phone || "",
    ...getSavedThemeValues(user?.company),
  };

  const handleImageChange = (file) => {
    if (!file) {
      setImgFile(null);
      setImgPreview(null);
      return;
    }

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.");
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      toast.error("File size must be less than 10MB.");
      return;
    }

    setImgFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImgPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImgFile(null);
    setImgPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      const toastId = toast.loading("Updating portal settings...");

      let logoUrl = user?.company?.logo_url;

      // Upload image if a new one is selected
      if (imgFile) {
        setIsUploadingImage(true);
        try {
          const uploadedUrl = await uploadImage(imgFile);
          if (uploadedUrl) {
            logoUrl = uploadedUrl;
            setIsUploadingImage(false);
          } else {
            toast.error("Image upload failed. Please try again.", { id: toastId });
            setIsUploadingImage(false);
            setSubmitting(false);
            return;
          }
        } catch (uploadError) {
          toast.error("Error uploading image. Please try again.", { id: toastId });
          setIsUploadingImage(false);
          setSubmitting(false);
          return;
        }
      }

      // Prepare payload
      // Include logo_url if a new image was uploaded, or if existing logo should be preserved
      const payload = {
        name: values.name,
        address: values.address,
        phone: values.phone,
        ...themeToApiPayload({
          primary: values.primary_color,
          secondary: values.secondary_color,
        }),
      };

      // Only include logo_url if we have a URL (either newly uploaded or existing)
      // If user didn't upload new image and there's no existing logo, omit it
      if (logoUrl) {
        payload.logo_url = logoUrl;
      }

      const response = await updatePortal(payload);

      if (response.error) {
        toast.error(
          response.error?.data?.message || "Failed to update portal settings",
          { id: toastId }
        );
        setSubmitting(false);
        return;
      }

      if (response.data?.status === false) {
        toast.error(
          response.data?.message || "Failed to update portal settings",
          { id: toastId }
        );
        setSubmitting(false);
        return;
      }

      if (response.data?.status === true) {
        toast.success("Portal settings updated successfully!", { id: toastId });
        
        // Update user data in Redux state with new company information
        if (user) {
          const updatedCompany = mergeThemeIntoCompany(
            {
              ...user.company,
              name: values.name,
              address: values.address,
              phone: values.phone,
              ...(logoUrl && { logo_url: logoUrl }),
            },
            {
              primary: values.primary_color,
              secondary: values.secondary_color,
            }
          );
          const updatedUserData = {
            ...user,
            company: updatedCompany,
          };
          dispatch(setUserData(updatedUserData));
          dispatch(setCoopData(updatedCompany));
          persistThemeFromCoopPayload(updatedCompany);
        }
        
        // Reset form state - clear imgFile first, but keep imgPreview temporarily
        // until component re-renders with updated user data
        setImgFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setIsEditing(false);
        
        // Clear imgPreview after a brief delay to allow Redux state update to propagate
        // This ensures the new logo from Redux is used instead of the preview
        setTimeout(() => {
          setImgPreview(null);
        }, 100);
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Portal settings update error:", error);
    } finally {
      setSubmitting(false);
      setIsUploadingImage(false);
    }
  };

  const handleCancel = (resetForm) => {
    setIsEditing(false);
    setImgFile(null);
    setImgPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    applyCompanyTheme(normalizeTheme(user?.company));
    resetForm?.({ values: initialValues });
  };

  const savedTheme = normalizeTheme(user?.company);

  // Get logo URL with cache-busting for remote images
  const getLogoUrl = () => {
    const logoUrl = imgPreview || user?.company?.logo_url || "/Staff/avatar.svg";
    // Add cache-busting parameter for remote images (not local paths)
    if (logoUrl && logoUrl.startsWith('http')) {
      return logoUrl.includes('?') 
        ? `${logoUrl}&t=${Date.now()}` 
        : `${logoUrl}?t=${Date.now()}`;
    }
    return logoUrl;
  };

  const currentLogoUrl = getLogoUrl();

  return (
    <section className="mx-2 mx-lg-4 min-vh-100 mb-3">
      <PageTitle title={"Portal Settings"} />
      <CardComp cardClassName={"pt-4"} cardBodyClassName={"py-0"}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting: formikSubmitting, setFieldValue, resetForm }) => {
            const isFormLoading = isSubmitting || isUploadingImage || formikSubmitting;

            const handleResetThemeDefaults = () => {
              setFieldValue("primary_color", DEFAULT_THEME.primary);
              setFieldValue("secondary_color", DEFAULT_THEME.secondary);
            };

            return (
              <Form>
                <DraftThemeApplicator
                  enabled={isEditing}
                  primaryColor={values.primary_color}
                  secondaryColor={values.secondary_color}
                />
                <div className="row gx-0">
                  <div className="col-12">
                    <div className="bg-white2 p-4 rounded">
                      {!isEditing ? (
                        // View Mode - Improved Layout
                        <div>
                          {/* Logo and Company Name Section */}
                          <div className="text-center mb-4">
                            <div className="d-flex justify-content-center mb-3">
                              <FallbackImage
                                width="120px"
                                height="120px"
                                src={currentLogoUrl}
                                style={{
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  border: "4px solid #e0e0e0",
                                }}
                              />
                            </div>
                            <h4 className="fw-bold mb-3 text-black1">
                              {user?.company?.name || "N/A"}
                            </h4>
                            <div className="d-flex justify-content-end">
                              <ButtonComp
                                btnText={"Edit"}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setIsEditing(true);
                                }}
                                btnClassName={
                                  "bg-primary1 text-white rounded py-2 px-4 fw-2"
                                }
                              />
                            </div>
                          </div>

                          {/* Divider */}
                          <hr className="my-4" style={{ borderColor: "#e0e0e0" }} />

                          {/* Company Details Grid */}
                          <div className="row g-4">
                            {/* Address Section */}
                            <div className="col-12 col-md-6">
                              <div className="d-flex align-items-start">
                                <div className="me-3">
                                  <i className="fas fa-map-marker-alt text-primary1" style={{ fontSize: "18px" }}></i>
                                </div>
                                <div>
                                  <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "10px", letterSpacing: "1px" }}>
                                    Address
                                  </p>
                                  <p className="mb-0 text-black1 fw-2">
                                    {user?.company?.address || "N/A"}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Phone Section */}
                            <div className="col-12 col-md-6">
                              <div className="d-flex align-items-start">
                                <div className="me-3">
                                  <i className="fas fa-phone text-primary1" style={{ fontSize: "18px" }}></i>
                                </div>
                                <div>
                                  <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "10px", letterSpacing: "1px" }}>
                                    Phone Number
                                  </p>
                                  <p className="mb-0 text-black1 fw-2">
                                    {user?.company?.phone || "N/A"}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Email Section */}
                            <div className="col-12 col-md-6">
                              <div className="d-flex align-items-start">
                                <div className="me-3">
                                  <i className="fas fa-envelope text-primary1" style={{ fontSize: "18px" }}></i>
                                </div>
                                <div>
                                  <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "10px", letterSpacing: "1px" }}>
                                    Email Address
                                  </p>
                                  <p className="mb-0 text-black1 fw-2">
                                    {user?.company?.email || "N/A"}
                                  </p>
                                  <small className="text-muted d-block mt-1">
                                    (Cannot be changed)
                                  </small>
                                </div>
                              </div>
                            </div>

                            {/* Domain Section */}
                            {user?.company?.domain_url && (
                              <div className="col-12 col-md-6">
                                <div className="d-flex align-items-start">
                                  <div className="me-3">
                                    <i className="fas fa-globe text-primary1" style={{ fontSize: "18px" }}></i>
                                  </div>
                                  <div>
                                    <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "10px", letterSpacing: "1px" }}>
                                      Domain URL
                                    </p>
                                    <p className="mb-0 text-black1 fw-2">
                                      {user?.company?.domain_url}
                                    </p>
                                    <small className="text-muted d-block mt-1">
                                      (Cannot be changed)
                                    </small>
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="col-12 col-md-6">
                              <div className="d-flex align-items-start">
                                <div className="me-3">
                                  <i className="fas fa-palette text-primary1" style={{ fontSize: "18px" }}></i>
                                </div>
                                <div>
                                  <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "10px", letterSpacing: "1px" }}>
                                    Brand colors
                                  </p>
                                  <div className="d-flex gap-3 align-items-center">
                                    <div className="d-flex align-items-center gap-2">
                                      <span
                                        style={{
                                          width: "24px",
                                          height: "24px",
                                          borderRadius: "6px",
                                          background: savedTheme.primary,
                                          border: "1px solid #e0e0e0",
                                        }}
                                      />
                                      <span className="text-black1 fw-2">{savedTheme.primary}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span
                                        style={{
                                          width: "24px",
                                          height: "24px",
                                          borderRadius: "6px",
                                          background: savedTheme.secondary,
                                          border: "1px solid #e0e0e0",
                                        }}
                                      />
                                      <span className="text-black1 fw-2">{savedTheme.secondary}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Edit Mode
                        <div className="row gx-4">
                          <div className="col-12 col-lg-6">
                            <div className="mb-3">
                              <TextInputComp
                                name="name"
                                LabelText={"Company Name"}
                                LabelClassName={"fw-2"}
                                placeholder={"Enter company name"}
                                borderColor="#A1A0A0"
                                required
                              />
                            </div>
                            <div className="mb-3">
                              <FormikTextAreaComp
                                fieldName="address"
                                LabelText={"Address"}
                                labelClassName={"fw-2"}
                                rows={4}
                                placeholder={"Enter company address"}
                                required
                              />
                            </div>
                            <div className="mb-3">
                              <TextInputComp
                                name="phone"
                                LabelText={"Phone Number"}
                                LabelClassName={"fw-2"}
                                placeholder={"Enter phone number (11 digits)"}
                                borderColor="#A1A0A0"
                                required
                              />
                            </div>

                            {/* Non-editable fields */}
                            <div className="mb-3">
                              <label className="form-label fw-2 text-muted mb-1">
                                Email Address
                                <small className="ms-2 text-muted">
                                  (Cannot be changed)
                                </small>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={user?.company?.email || "N/A"}
                                disabled
                                style={{
                                  background: "#f7f7f7",
                                  cursor: "not-allowed",
                                }}
                              />
                            </div>
                            {user?.company?.domain_url && (
                              <div className="mb-3">
                                <label className="form-label fw-2 text-muted mb-1">
                                  Domain URL
                                  <small className="ms-2 text-muted">
                                    (Cannot be changed)
                                  </small>
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={user?.company?.domain_url}
                                  disabled
                                  style={{
                                    background: "#f7f7f7",
                                    cursor: "not-allowed",
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          <div className="col-12 col-lg-6">
                            <ThemeBrandingEditor
                              primaryColor={values.primary_color}
                              secondaryColor={values.secondary_color}
                              onPrimaryChange={(color) => setFieldValue("primary_color", color)}
                              onSecondaryChange={(color) => setFieldValue("secondary_color", color)}
                              onResetDefaults={handleResetThemeDefaults}
                              logoUrl={currentLogoUrl}
                              companyName={values.name || user?.company?.name}
                            />

                            <div className="mb-4">
                              <label className="form-label fw-2 mb-2">Company Logo</label>
                              <div className="d-flex align-items-start gap-3">
                                <div className="position-relative">
                                  <FallbackImage
                                    width="100px"
                                    height="100px"
                                    src={currentLogoUrl}
                                    style={{
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      border: "3px solid #e0e0e0",
                                    }}
                                  />
                                  {isUploadingImage && (
                                    <div
                                      className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                      style={{
                                        background: "rgba(0,0,0,0.5)",
                                        borderRadius: "50%",
                                      }}
                                    >
                                      <div className="spinner-border spinner-border-sm text-white" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-grow-1">
                                  <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleImageChange(e.target.files?.[0])}
                                    className="form-control mb-2"
                                    disabled={isFormLoading}
                                  />
                                  {imgFile && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={handleRemoveImage}
                                      disabled={isFormLoading}
                                    >
                                      Remove Image
                                    </button>
                                  )}
                                  <small className="d-block text-muted mt-2">
                                    Supported formats: JPEG, PNG, GIF, WebP (Max 10MB)
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-12">
                            <div className="d-flex gap-3 justify-content-end mt-3">
                              <ButtonComp
                                btnText={"Cancel"}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCancel(resetForm);
                                }}
                                btnClassName={
                                  "border border-primary1 text-primary1 bg-transparent rounded py-2 px-4 fw-2"
                                }
                                disabled={isFormLoading}
                              />
                              <ButtonComp
                                btnText={isFormLoading ? "Saving..." : "Save Changes"}
                                type="submit"
                                btnClassName={
                                  "bg-primary1 text-white rounded py-2 px-4 fw-2"
                                }
                                disabled={isFormLoading}
                                loading={isFormLoading}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </CardComp>
    </section>
  );
}